import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { downloadDatasetExcel, logDatasetView, fetchDatasetStats, fetchDatasetVersions } from '../services/datasetService';
import { endpoints } from '../api/endpoints';
import { Dropdown } from 'react-bootstrap';
import { FaFileExcel, FaFileCsv, FaImages, FaDownload, FaHistory, FaSearch, FaExternalLinkAlt, FaDatabase, FaCalendarAlt, FaHashtag, FaInfoCircle, FaShieldAlt } from 'react-icons/fa';
import '../styles/dataset.css';
import { useNotification } from '../components/framework/NotificationContext';
import DatasetVersionsList from '../components/dataset/DatasetVersionsList';
import SEO from '../components/SEO';

const DatasetPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ views: 0, downloads: 0 });
  const [latestVersion, setLatestVersion] = useState(null);
  const [mainCitation, setMainCitation] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const { error } = useNotification();

  useEffect(() => {
    // Log view on mount
    logDatasetView();

    // Fetch stats
    const loadStats = async () => {
      const data = await fetchDatasetStats();
      if (data) {
        setStats(data);
      }
    };
    loadStats();

    // Fetch latest version metadata
    const loadVersions = async () => {
      try {
        const versions = await fetchDatasetVersions();
        if (versions && versions.length > 0) {
          setLatestVersion(versions[0]);
        }
      } catch (err) {
        console.error("Failed to load versions for metadata", err);
      }
    };
    loadVersions();

    // Fetch main citation
    fetch('/citations.json')
      .then(response => response.json())
      .then(data => {
        if (data.categories && data.categories.length > 0) {
          const mainCat = data.categories.find(c => c.id === 'main-citations');
          if (mainCat && mainCat.citations && mainCat.citations.length > 0) {
            setMainCitation(mainCat.citations[0]);
          }
        }
      })
      .catch(err => console.error("Error fetching citation for dataset page:", err));
  }, []);

  const handleDownload = async (type = 'excel') => {
    try {
      if (type === 'excel') {
        const blob = await downloadDatasetExcel();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'electrospinning_dataset.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        let url;
        if (type === 'images') {
          url = endpoints.downloadImages;
        } else if (type === 'csv') {
          url = endpoints.downloadCsv;
        }

        if (url) {
          window.location.href = url;
        }
      }

      setTimeout(async () => {
        const newStats = await fetchDatasetStats();
        if (newStats) setStats(newStats);
      }, 1000);

    } catch (err) {
      console.error('Error downloading file:', err.message);
      error('There was an error downloading the dataset. Please try again later.');
    }
  };

  const handlePreviewRedirect = () => {
    navigate('/PreviewDatasetPage');
  };

  return (
    <div className="container py-4">
      <SEO
        title="Dataset Hub"
        description="Explore and download curated electrospinning experimental datasets. Standardized data for research, meta-analysis, and machine learning."
      />
      <header className="dataset-hero">
        <h1 className="display-4 dataset-title">Electrospinning Dataset Hub</h1>
        <p className="dataset-intro">
          A structured dataset compiled from published electrospinning experiments.<br />
          Browse the data online or download the master dataset for scientific research.
        </p>
      </header>

      {/* Page Level Stats */}
      <div className="d-flex justify-content-center gap-4 mb-4">
        <div className="stat-card">
          <div className="stat-icon-wrapper view-icon-bg">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="stat-icon" viewBox="0 0 16 16">
              <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
              <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats ? stats.views : 0}</h3>
            <span className="stat-label">Views</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper download-icon-bg">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="stat-icon" viewBox="0 0 16 16">
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
            </svg>
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats ? stats.downloads : 0}</h3>
            <span className="stat-label">Downloads</span>
          </div>
        </div>
      </div>

      <main>
        {/* Full-width Master Dataset Metadata Card */}
        {latestVersion && (
          <div className="master-dataset-card mb-4">
            <div className="master-dataset-header">
              <h2 className="master-dataset-title">
                <FaDatabase className="text-primary me-2" /> Master Dataset &bull; Release {latestVersion.versionIdentifier}
              </h2>
              <span className="badge bg-success px-3 py-2" style={{ fontSize: '0.75rem', borderRadius: '20px' }}>
                LATEST PUBLIC RELEASE
              </span>
            </div>

            <div className="master-dataset-pills">
              <div className="master-dataset-pill">
                <FaHashtag className="text-secondary" />
                <span><strong>Records:</strong> {latestVersion.recordCount} entries</span>
              </div>

              {latestVersion.createdAt && (
                <div className="master-dataset-pill">
                  <FaCalendarAlt className="text-secondary" />
                  <span><strong>Released:</strong> {new Date(latestVersion.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                </div>
              )}

              {latestVersion.doi && (
                <div className="master-dataset-pill">
                  <strong>DOI:</strong>
                  <a
                    href={`https://doi.org/${latestVersion.doi}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-navy fw-semibold text-decoration-none"
                  >
                    {latestVersion.doi} <FaExternalLinkAlt className="ms-1" style={{ fontSize: '0.75rem' }} />
                  </a>
                </div>
              )}

              {latestVersion.license && (
                <div className="master-dataset-pill">
                  <FaShieldAlt className="text-secondary me-1" />
                  <span><strong>License:</strong> {latestVersion.license}</span>
                </div>
              )}
            </div>

            {latestVersion.description && (
              <div className="master-dataset-desc">
                <FaInfoCircle className="me-2 text-primary" />
                {latestVersion.description}
              </div>
            )}
          </div>
        )}

        {/* Balanced Action Grid (Neither Card Overstretched) */}
        <div className="row g-4 mb-4 justify-content-center align-items-stretch" style={{ maxWidth: '920px', margin: '0 auto' }}>
          {/* Preview Card */}
          <div className="col-md-6 d-flex">
            <div className="action-card w-100 d-flex flex-column">
              <div className="action-icon">
                <FaSearch />
              </div>
              <h2 className="action-title">Preview Dataset</h2>
              <p className="action-desc mb-4">
                Explore and filter the experimental dataset directly in your browser before downloading.
              </p>
              
              <div className="mt-auto w-100">
                <button onClick={handlePreviewRedirect} className="action-btn btn-navy-outline w-100">
                  Open Dataset Preview
                </button>
              </div>
            </div>
          </div>

          {/* Download Card */}
          <div className="col-md-6 d-flex">
            <div className="action-card w-100 d-flex flex-column">
              <div className="action-icon">
                <FaDownload />
              </div>
              <h2 className="action-title">Download Dataset</h2>
              <p className="action-desc mb-3">
                Download the complete dataset: one row per run, every value in a fixed unit, with a data dictionary.
              </p>

              <div className="mt-auto w-100">
                <Dropdown className="w-100">
                  <Dropdown.Toggle className="action-btn btn-navy w-100 d-flex align-items-center justify-content-center mx-auto" id="download-dropdown">
                    <FaDownload className="me-2" /> Download Dataset
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="w-100 download-menu">
                    {[
                      { type: 'excel', icon: <FaFileExcel style={{ color: '#15803d' }} />, title: 'Excel workbook (.xlsx)', hint: 'Runs, publications, README and data dictionary' },
                      { type: 'csv', icon: <FaFileCsv style={{ color: '#15803d' }} />, title: 'CSV table (.csv)', hint: 'The runs, for Python, R or any analysis tool' },
                      { type: 'images', icon: <FaImages style={{ color: '#1d6fd1' }} />, title: 'Image dataset (.zip)', hint: 'Fiber images with an index of their runs (images.csv)' },
                    ].map(o => (
                      <Dropdown.Item key={o.type} onClick={() => handleDownload(o.type)} className="py-2" style={{ whiteSpace: 'normal' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', width: '100%' }}>
                          <span style={{ fontSize: '1.15rem', lineHeight: 1.2 }}>{o.icon}</span>
                          <span style={{ display: 'block' }}>
                            <span style={{ display: 'block', fontWeight: 600 }}>{o.title}</span>
                            <span style={{ display: 'block', fontSize: '0.8rem', color: '#64748b' }}>{o.hint}</span>
                          </span>
                        </div>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <div className="terms-notice-wrapper mt-2">
                  <p className="terms-notice-text mb-0" style={{ fontSize: '0.75rem' }}>
                    By downloading, you agree to the
                    <Link to="/terms" className="terms-link mx-1">Terms</Link>
                    &
                    <Link to="/license" className="terms-link mx-1">License</Link>.
                  </p>
                </div>

                <div className="mt-2 pt-2 border-top w-100">
                  <button
                    className="btn btn-link text-navy text-decoration-none small d-flex align-items-center justify-content-center mx-auto transition-all p-0"
                    onClick={() => setShowArchiveModal(true)}
                    style={{ fontSize: '0.825rem', fontWeight: 500 }}
                  >
                    <FaHistory className="me-1" /> View Release Archive
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Citation Section */}
        {mainCitation && (
          <section className="citation-section text-center">
            <h3 className="citation-header justify-content-center">
              Citation
            </h3>
            <p className="text-muted small mb-3">
              If you use this dataset or platform in your research, please cite the following work:
            </p>
            <div className="citation-box text-start mb-3">
              {mainCitation.citation}
              <br /><br />
              <strong>DOI:</strong> <a
                href={mainCitation.url}
                target="_blank"
                rel="noopener noreferrer"
                className="citation-doi"
              >
                {mainCitation.doi}
              </a>
            </div>
            <Link to="/how-to-cite" className="btn btn-link text-decoration-none">
              View full attribution guidelines &rarr;
            </Link>
          </section>
        )}

      </main>

      <DatasetVersionsList
        show={showArchiveModal}
        onHide={() => setShowArchiveModal(false)}
      />

    </div>
  );
};

export default DatasetPage;